import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { FileText, Download, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function fmtINR(n) { return `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`; }
function monthLabel(m) { return dayjs(`${m}-01`).format("MMMM YYYY"); }

const STATUS_LABEL = {
  file_generated: "File generated",
  uploaded_to_bank: "Uploaded to bank",
  paid: "Paid",
};
const STATUS_STYLE = {
  file_generated: "bg-zinc-100 text-zinc-700 border-zinc-200",
  uploaded_to_bank: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function PayslipsPage({ embedded = false }) {
  const { user } = useAuth();
  // Payroll is admin-only. Managers see just their own payslips, like anyone else.
  const isAdminOrMgr = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterEmp, setFilterEmp] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [genMonth, setGenMonth] = useState(dayjs().format("YYYY-MM"));
  const [busy, setBusy] = useState(false);
  const [payments, setPayments] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [paymentDate, setPaymentDate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
  const [missingDialog, setMissingDialog] = useState(null);
  const [regen, setRegen] = useState(null);
  const [regenDays, setRegenDays] = useState("");

  const daysInMonth = (m) =>
    new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0).getDate();

  // Regenerating rebuilds the month from the salary on file. Leaving the days
  // box empty pays a whole month; a figure pro-rates the fixed components.
  const doRegen = async () => {
    if (!regen) return;
    setBusy(true);
    try {
      await api.post("/payslips/generate-one", {
        employee_id: regen.employee_id,
        month: regen.month,
        days: regenDays === "" ? null : Number(regenDays),
        replace: true,
      });
      toast.success(`Rebuilt ${regen.month} for ${regen.employee_snapshot?.name || ""}`);
      setRegen(null);
      setRegenDays("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const load = async () => {
    try {
      if (isAdminOrMgr) {
        const params = {};
        if (filterEmp !== "all") params.employee_id = filterEmp;
        if (filterMonth !== "all") params.month = filterMonth;
        const { data } = await api.get("/payslips", { params });
        setRows(data);
      } else {
        const { data } = await api.get("/payslips/my");
        setRows(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [filterEmp, filterMonth, user]);
  useEffect(() => {
    if (!isAdminOrMgr) return;
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [isAdminOrMgr]);

  const months = useMemo(() => {
    const set = new Set(rows.map((r) => r.month));
    // include next 12 months back from now for filter dropdown
    for (let i = 0; i < 12; i++) set.add(dayjs().subtract(i, "month").format("YYYY-MM"));
    return Array.from(set).sort().reverse();
  }, [rows]);

  const download = (ps) => {
    const url = `${API_BASE}/payslips/${ps.id}/pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const loadPayments = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/payments");
      setPayments(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadPayments(); }, [isAdmin]);

  const exportEnet = async () => {
    setExporting(true);
    try {
      const dateStr = dayjs(paymentDate).format("DD/MM/YYYY");
      const url = `${API_BASE}/payroll/enet-export?month=${encodeURIComponent(genMonth)}&payment_date=${encodeURIComponent(dateStr)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body.detail;
        if (detail && typeof detail === "object" && detail.missing) {
          setMissingDialog({ message: detail.message, missing: detail.missing });
        } else {
          toast.error(typeof detail === "string" ? detail : "Export failed");
        }
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(disposition);
      const filename = m ? m[1] : `ENET_Payroll_${genMonth}.xlsx`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${filename}`);
      loadPayments();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setExporting(false); }
  };

  const updateStatus = async (log, newStatus) => {
    try {
      const { data } = await api.patch(`/payments/${log.id}`, { status: newStatus });
      if (newStatus === "paid") {
        toast.success(`Marked as Paid — released ${data.released_payslips} payslip(s)`);
      } else {
        toast.success(`Status updated to ${STATUS_LABEL[newStatus]}`);
      }
      loadPayments();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        {!embedded && (
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Payslips</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {isAdminOrMgr ? "View, generate and download employee payslips." : "Your monthly payslips — download as PDF."}
          </p>
        </div>
        )}
        {isAdmin && (
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-xs text-zinc-600">Month</Label>
              <input
                type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)}
                className="mt-1 h-9 rounded-md border border-zinc-300 px-3 text-sm"
                data-testid="generate-month-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Payment date</Label>
              <input
                type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1 h-9 rounded-md border border-zinc-300 px-3 text-sm"
                data-testid="payment-date-input"
              />
            </div>
            <Button onClick={exportEnet} disabled={exporting} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-9" data-testid="export-enet-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> {exporting ? "Exporting…" : "Export bank file (ENet)"}
            </Button>
          </div>
        )}
      </div>

      {isAdminOrMgr && (
        <div className="flex flex-wrap gap-3">
          <div>
            <Label className="text-xs text-zinc-600">Employee</Label>
            <Select value={filterEmp} onValueChange={setFilterEmp}>
              <SelectTrigger className="h-9 w-56 mt-1" data-testid="filter-employee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.employee_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-600">Month</Label>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="h-9 w-44 mt-1" data-testid="filter-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <Card className="border-zinc-200 shadow-none">
          <CardContent className="py-16 text-center">
            <FileText className="h-10 w-10 text-zinc-300 mx-auto mb-3" strokeWidth={1.3} />
            <div className="text-sm font-medium text-zinc-800">No payslips yet</div>
            <div className="text-xs text-zinc-500 mt-1">
              {isAdmin ? "Run payroll from Employees > Payroll." : "Payslips are generated automatically on the 1st of each month."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-zinc-200 shadow-none">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdminOrMgr && <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Employee</TableHead>}
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Month</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Net / Payout</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Generated</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`payslip-row-${p.id}`}>
                    {isAdminOrMgr && (
                      <TableCell className="text-sm font-medium">
                        {p.employee_snapshot?.name}
                        <div className="text-xs text-zinc-500">{p.employee_snapshot?.employee_code}</div>
                      </TableCell>
                    )}
                    <TableCell className="text-sm font-medium">{monthLabel(p.month)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[11px] capitalize ${p.employment_type_snapshot === "intern" ? "border-amber-200 text-amber-700 bg-amber-50" : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
                        {p.employment_type_snapshot === "intern" ? "Intern" : "Full-Time"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums font-semibold text-emerald-700">{fmtINR(p.total_payment ?? p.net_pay)}{p.reimbursements ? (<div className="text-[11px] font-normal text-zinc-500">net {fmtINR(p.net_pay)} + {fmtINR(p.reimbursements)} reimbursed</div>) : null}</TableCell>
                    <TableCell className="text-sm text-zinc-500">{dayjs(p.generated_at).format("D MMM YYYY")}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => download(p)} className="h-8 border-zinc-300" data-testid={`download-payslip-${p.id}`}>
                        <Download className="h-4 w-4 mr-1" /> Download PDF
                      </Button>
                      {isAdminOrMgr && (
                        <Button size="sm" variant="outline" className="h-8 border-zinc-300 ml-2"
                                onClick={() => { setRegen(p); setRegenDays(String(p.days_worked ?? "")); }}
                                data-testid={`regen-payslip-${p.id}`}>
                          Regenerate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {isAdmin && payments.length > 0 && (
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Payment log</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Month</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">File</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Payment date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Employees</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Update status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((log) => (
                    <TableRow key={log.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`payment-log-${log.id}`}>
                      <TableCell className="text-sm font-medium">{monthLabel(log.month)}</TableCell>
                      <TableCell className="text-xs text-zinc-500 font-mono">{log.filename}</TableCell>
                      <TableCell className="text-sm">{log.payment_date}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums">{fmtINR(log.total_amount)}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums">{log.employee_count}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[11px] ${STATUS_STYLE[log.status] || ""}`}>
                          {STATUS_LABEL[log.status] || log.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={log.status} onValueChange={(v) => updateStatus(log, v)}>
                          <SelectTrigger className="h-8 w-44" data-testid={`payment-status-select-${log.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="file_generated">File generated</SelectItem>
                            <SelectItem value="uploaded_to_bank">Uploaded to bank</SelectItem>
                            <SelectItem value="paid">Paid (release payslips)</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!missingDialog} onOpenChange={(v) => !v && setMissingDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Export blocked
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-600">{missingDialog?.message}</div>
          <div className="max-h-80 overflow-y-auto border border-zinc-200 rounded-md divide-y divide-zinc-100" data-testid="missing-list">
            {missingDialog?.missing?.map((m) => (
              <div key={m.employee_code} className="px-3 py-2.5 text-sm flex items-center justify-between">
                <div>
                  <div className="font-medium text-zinc-800">{m.name}</div>
                  <div className="text-xs text-zinc-500">{m.employee_code}</div>
                </div>
                <div className="text-xs text-red-600 tabular-nums text-right">
                  {m.missing_fields.map((f) => f.replace(/_/g, " ")).join(", ")}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingDialog(null)} className="border-zinc-300">Close</Button>
            <Button onClick={() => { setMissingDialog(null); window.location.href = "/employees"; }} className="bg-emerald-600 hover:bg-emerald-700">
              Fix in Employees
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    <Dialog open={!!regen} onOpenChange={(o) => { if (!o) { setRegen(null); setRegenDays(""); } }}>
      <DialogContent className="max-w-md" data-testid="regen-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            Rebuild {regen?.month} for {regen?.employee_snapshot?.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-600">
          This replaces the payslip using the salary currently on file. A released
          payslip cannot be rebuilt. Leave days blank to pay a whole month.
        </p>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-zinc-600">Days paid</label>
            <input type="number" min="0" max={regen ? daysInMonth(regen.month) : 31}
                   value={regenDays} onChange={(e) => setRegenDays(e.target.value)}
                   placeholder="full month" data-testid="regen-days"
                   className="w-28 border border-zinc-300 rounded px-2 py-1 text-sm" />
          </div>
          <span className="text-[11px] text-zinc-600 pb-2">
            of {regen ? daysInMonth(regen.month) : ""} days
          </span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setRegen(null)}>Cancel</Button>
            <Button size="sm" disabled={busy} onClick={doRegen} data-testid="regen-confirm">
              {busy ? "Working\u2026" : "Rebuild"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </div>
  );
}
